import { FC, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { colors, fontStyles, spacing } from '../../../styles';
import { getTranscript } from '../../../apis/backend';
import Header from '../../Header';
import Page from '../../Page';
import SafeArea from '../../SafeArea';
import ErrorSection from '../../ErrorSection';
import SkeletonBox from '../../SkeletonBox';
import magnifyingGlass from '../../Search/magnifyingGlass.svg';

interface Props {
  symbol: string;
}

// Utility function to add double line breaks every two sentences
const formatTextWithSpacing = (text: string) => {
  // Split by sentences (periods, exclamation marks, question marks)
  const sentences = text.split(/(?<=[.!?])\s+/);
  const formattedSentences = [];

  for (let i = 0; i < sentences.length; i++) {
    formattedSentences.push(sentences[i]);

    // Add double line break after every two sentences (but not at the end)
    if ((i + 1) % 2 === 0 && i < sentences.length - 1) {
      formattedSentences.push('\n\n');
    } else if (i < sentences.length - 1) {
      formattedSentences.push(' ');
    }
  }

  return formattedSentences.join('');
};

// Utility function to extract relevant paragraphs containing search terms
const extractRelevantParagraphs = (text: string, searchTerm: string) => {
  if (!searchTerm.trim()) return text;

  // Split into paragraphs (by double line breaks or sentence groups)
  const paragraphs = text.split(/\n\n+/);
  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(escapedSearchTerm, 'gi');

  const relevantParagraphs = paragraphs.filter((paragraph) => regex.test(paragraph));

  return relevantParagraphs.length > 0 ? relevantParagraphs.join('\n\n') : '';
};

// Utility function to highlight search terms in text
const highlightText = (text: string, searchTerm: string) => {
  if (!searchTerm.trim()) return text;

  // Escape special regex characters in search term
  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');

  const parts = text.split(regex);

  return parts.map((part, index) => {
    const isMatch = regex.test(part);
    regex.lastIndex = 0; // Reset regex state

    if (isMatch) {
      return (
        <span
          key={index}
          css={css`
            background-color: yellow;
            color: black;
            padding: 1px 2px;
            border-radius: 2px;
          `}
        >
          {part}
        </span>
      );
    }
    return part;
  });
};

const TranscriptPage: FC<Props> = ({ symbol }) => {
  const history = useHistory();
  const params = new URLSearchParams(window.location.search);
  const year = parseInt(params.get('year') || '0');
  const quarter = parseInt(params.get('quarter') || '0');
  const summary = params.get('summary') || null;
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Helper function to toggle expand/collapse state
  const toggleExpanded = (index: number) => {
    const newExpandedCards = new Set(expandedCards);
    if (newExpandedCards.has(index)) {
      newExpandedCards.delete(index);
    } else {
      newExpandedCards.add(index);
    }
    setExpandedCards(newExpandedCards);
  };

  const { data, isLoading, error } = useQuery(
    ['transcript', symbol, year, quarter],
    async () => {
      if (!year || !quarter) return null;
      return await getTranscript(symbol, year, quarter);
    },
    {
      enabled: !!(year && quarter),
    },
  );

  if (!year || !quarter) {
    return (
      <SafeArea>
        <Page>
          <Header headingText="" onBack={() => history.goBack()} />
          <ErrorSection text="Year and quarter parameters are required." onRetry={() => history.goBack()} />
        </Page>
      </SafeArea>
    );
  }

  if (isLoading) {
    return (
      <SafeArea>
        <Page>
          <Header headingText="Transcript" onBack={() => history.goBack()} />
          <div css={styles.container}>
            <div css={styles.skeletonMargin}>
              <SkeletonBox width="100%" />
            </div>
            <div css={styles.skeletonMargin}>
              <SkeletonBox width="100%" />
            </div>
            <div css={styles.skeletonMargin}>
              <SkeletonBox width="100%" />
            </div>
            <div css={styles.skeletonMargin}>
              <SkeletonBox width="100%" />
            </div>
          </div>
        </Page>
      </SafeArea>
    );
  }

  if (error || !data) {
    return (
      <SafeArea>
        <Page>
          <Header headingText="Transcript" onBack={() => history.goBack()} />
          <ErrorSection
            text="The earnings call transcript could not be loaded at this time."
            onRetry={() => window.location.reload()}
          />
        </Page>
      </SafeArea>
    );
  }

  // Filter transcript entries based on search term
  const filteredTranscript = data.transcript.filter((entry) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      entry.speaker.toLowerCase().includes(searchLower) ||
      entry.title.toLowerCase().includes(searchLower) ||
      entry.content.toLowerCase().includes(searchLower)
    );
  });

  // Helper function to get display content based on search and expand state
  const getDisplayContent = (content: string, index: number) => {
    const formattedContent = formatTextWithSpacing(content);

    if (!searchTerm) {
      // No search term - show formatted content
      return formattedContent;
    }

    // Search term exists - show relevant paragraphs or full content if expanded
    const isExpanded = expandedCards.has(index);
    if (isExpanded) {
      return formattedContent;
    } else {
      const relevantText = extractRelevantParagraphs(formattedContent, searchTerm);
      return relevantText || formattedContent; // Fallback to full content if no relevant paragraphs
    }
  };

  // Check if content should show expand/collapse button
  const shouldShowExpandButton = (content: string) => {
    if (!searchTerm) return false;

    const formattedContent = formatTextWithSpacing(content);
    const relevantText = extractRelevantParagraphs(formattedContent, searchTerm);

    // Show expand button if relevant text is shorter than full content
    return relevantText && relevantText.length < formattedContent.length;
  };

  const getSentimentIndicator = (sentiment: string) => {
    const sentimentValue = parseFloat(sentiment);
    if (sentimentValue > 0.2) {
      return { emoji: '🟢', label: 'Positive sentiment' };
    }
    if (sentimentValue < -0.2) {
      return { emoji: '🔴', label: 'Negative sentiment' };
    }
    return { emoji: '', label: 'Neutral sentiment' };
  };

  return (
    <SafeArea>
      <Page>
        <Header headingText="Transcript" onBack={() => history.goBack()} />
        <div css={styles.container}>
          <div css={styles.header}>
            <h1 css={styles.title}>
              {symbol} {data.quarter} Earnings Call
            </h1>

            {summary && (
              <div css={styles.summaryContainer}>
                <div css={styles.summaryLabel}>Summary</div>
                <div css={styles.summaryText}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary}</ReactMarkdown>
                </div>
              </div>
            )}

            <div css={styles.searchContainer}>
              <img src={magnifyingGlass} css={styles.searchIcon} alt="" />
              <input
                type="text"
                placeholder="Search transcript..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                css={styles.searchInput}
              />
            </div>
          </div>

          {filteredTranscript.length === 0 ? (
            <ErrorSection
              text={searchTerm ? `No transcript entries found for "${searchTerm}"` : 'No transcript data available.'}
              onRetry={() => setSearchTerm('')}
            />
          ) : (
            <div css={styles.transcriptContainer}>
              {filteredTranscript.map((entry, index) => {
                const displayContent = getDisplayContent(entry.content, index);
                const showExpandButton = shouldShowExpandButton(entry.content);
                const isExpanded = expandedCards.has(index);

                return (
                  <div key={index} css={styles.speakerCard}>
                    <div css={styles.speakerHeader}>
                      <div css={styles.speakerInfo}>
                        <div css={styles.speakerName}>{highlightText(entry.speaker, searchTerm)}</div>
                        {entry.title && <div css={styles.speakerTitle}>{highlightText(entry.title, searchTerm)}</div>}
                      </div>
                      <div css={styles.sentimentIndicator} title={getSentimentIndicator(entry.sentiment).label}>
                        {getSentimentIndicator(entry.sentiment).emoji}
                      </div>
                    </div>
                    <div css={styles.speakerContent}>
                      <div css={styles.contentText}>{highlightText(displayContent, searchTerm)}</div>
                      {showExpandButton && (
                        <button onClick={() => toggleExpanded(index)} css={styles.expandButton}>
                          {isExpanded ? 'Show less' : 'Show more'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Page>
    </SafeArea>
  );
};

const styles = {
  container: css`
    padding: ${spacing.milli} ${spacing.centi} ${spacing.regular};
  `,
  header: css`
    margin-bottom: ${spacing.regular};
  `,
  title: css`
    margin: 0 0 ${spacing.regular} 0;
    ${fontStyles.shark}
    color: ${colors.liquorice};

    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `,
  summaryContainer: css`
    background: ${colors.snow};
    border: 1px solid ${colors.cloud};
    border-radius: 8px;
    padding: ${spacing.regular};
    margin-bottom: ${spacing.regular};

    html[data-theme='dark'] & {
      background: ${colors.liquorice};
      border-color: ${colors.tungsten};
    }
  `,
  summaryLabel: css`
    ${fontStyles.roboHorse}
    color: ${colors.petrolBlue};
    margin-bottom: ${spacing.centi};

    html[data-theme='dark'] & {
      color: ${colors.skyBlue};
    }
  `,
  summaryText: css`
    margin: 0;
    ${fontStyles.cat}
    color: ${colors.tungsten};
    line-height: 1.6;

    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }

    /* Markdown styling */
    p {
      margin: ${spacing.centi} 0;

      &:first-of-type {
        margin-top: 0;
      }

      &:last-of-type {
        margin-bottom: 0;
      }
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin: ${spacing.regular} 0 ${spacing.centi} 0;
      ${fontStyles.roboCat}
      color: ${colors.liquorice};

      &:first-of-type {
        margin-top: 0;
      }

      html[data-theme='dark'] & {
        color: ${colors.sugar};
      }
    }

    h2 {
      ${fontStyles.roboHorse}
    }

    h3 {
      ${fontStyles.roboCat}
    }

    ul,
    ol {
      margin: ${spacing.centi} 0;
      padding-left: ${spacing.regular};
    }

    li {
      margin: ${spacing.milli} 0;
    }

    strong {
      font-weight: 600;
      color: ${colors.liquorice};

      html[data-theme='dark'] & {
        color: ${colors.sugar};
      }
    }

    em {
      font-style: italic;
    }

    code {
      background: ${colors.fog};
      padding: 2px 6px;
      border-radius: 4px;
      font-family: monospace;
      font-size: 0.9em;

      html[data-theme='dark'] & {
        background: ${colors.tungsten};
      }
    }

    blockquote {
      margin: ${spacing.centi} 0;
      padding-left: ${spacing.regular};
      border-left: 3px solid ${colors.petrolBlue};
      color: ${colors.silver};

      html[data-theme='dark'] & {
        border-left-color: ${colors.skyBlue};
      }
    }
  `,
  searchContainer: css`
    position: relative;
    margin-bottom: ${spacing.centi};
  `,
  searchIcon: css`
    position: absolute;
    left: ${spacing.centi};
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    opacity: 0.6;
  `,
  searchInput: css`
    width: 100%;
    padding: ${spacing.centi} ${spacing.centi} ${spacing.centi} ${spacing.regular};
    border: 1px solid ${colors.cloud};
    border-radius: 8px;
    ${fontStyles.cat}
    color: ${colors.liquorice};
    background: ${colors.snow};

    &::placeholder {
      color: ${colors.silver};
    }

    &:focus {
      outline: none;
      border-color: ${colors.petrolBlue};
      box-shadow: 0 0 0 2px ${colors.petrolBlue}20;
    }

    html[data-theme='dark'] & {
      background: ${colors.tungsten};
      border-color: ${colors.silver};
      color: ${colors.sugar};

      &::placeholder {
        color: ${colors.silver};
      }

      &:focus {
        border-color: ${colors.skyBlue};
        box-shadow: 0 0 0 2px ${colors.skyBlue}20;
      }
    }
  `,
  transcriptContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${spacing.regular};
  `,
  speakerCard: css`
    background: ${colors.snow};
    border-radius: 12px;
    padding: ${spacing.regular};
    border: 1px solid ${colors.cloud};

    html[data-theme='dark'] & {
      background: ${colors.liquorice};
      border-color: ${colors.tungsten};
    }
  `,
  speakerHeader: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: ${spacing.centi};
    padding-bottom: ${spacing.centi};
    border-bottom: 1px solid ${colors.fog};

    html[data-theme='dark'] & {
      border-bottom-color: ${colors.tungsten};
    }
  `,
  speakerInfo: css`
    flex: 1;
  `,
  speakerName: css`
    ${fontStyles.roboHorse}
    color: ${colors.petrolBlue};
    margin-bottom: ${spacing.milli};

    html[data-theme='dark'] & {
      color: ${colors.skyBlue};
    }
  `,
  speakerTitle: css`
    ${fontStyles.roboCat}
    color: ${colors.silver};

    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `,
  sentimentIndicator: css`
    font-size: 1.2rem;
    margin-left: ${spacing.centi};
  `,
  speakerContent: css`
    ${fontStyles.cat}
    color: ${colors.tungsten};
    line-height: 1.6;

    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `,
  contentText: css`
    white-space: pre-wrap; /* Preserve line breaks from formatting */
    margin-bottom: ${spacing.centi};
  `,
  expandButton: css`
    background: none;
    border: 1px solid ${colors.petrolBlue};
    color: ${colors.petrolBlue};
    padding: ${spacing.milli} ${spacing.centi};
    border-radius: 6px;
    cursor: pointer;
    ${fontStyles.roboCat}
    transition: all 0.2s ease;

    &:hover {
      background: ${colors.petrolBlue};
      color: white;
    }

    html[data-theme='dark'] & {
      border-color: ${colors.skyBlue};
      color: ${colors.skyBlue};

      &:hover {
        background: ${colors.skyBlue};
        color: ${colors.liquorice};
      }
    }
  `,
  skeletonMargin: css`
    margin-bottom: ${spacing.regular};
  `,
};

export default TranscriptPage;
